import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  DocumentEntityTypesEnum,
  DocumentService,
  DocumentTypesEnum,
  PropertyService,
  PropertyItemVM,
} from "@propertelligent/client-api";
import { useForm, FormProvider, Controller } from "react-hook-form";

import Card from "../../newComponents/card";
import InputField from "../../newComponents/form-fields/InputField";
import Uploader from "../../newComponents/CustomUploader/Uploader";
import {
  JPEG_PNG_EXTENSIONS,
  JPEG_PNG_MIME_TYPES,
} from "../../newComponents/CustomUploader/imageUploadConstants";
import ThingsToKnow from "../../newComponents/info/things-to-know-demo";
import Radio from "../../newComponents/form-fields/radio";
import AddressAutocompleteRHF from "../../newComponents/AutoComplete/AddressAutocompleteRHF";
import apiHelper from "../../utils/apiHelper";
import FixedFooter from "../../newComponents/listings/add-listing-footer/fixed-footer";
import { ConfirmationPopup } from "../../newComponents/confirmation-popup/confirmation-popup-demo";

interface UnitFormValues {
  propertyType: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  unitImage: File | null;
}

const SelectProperty = ({ ptyTypeId, setPtyTypeId, navigateto, fetchData }) => {
  const methods = useForm<UnitFormValues>({
    mode: "onChange",
    defaultValues: {
      propertyType: "",
      address: "",
      city: "",
      state: "",
      zip: "",
      unitImage: null,
    },
  });

  const { handleSubmit, control, formState, watch, reset } = methods;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isUnsavedChangesOpen, setIsUnsavedChangesOpen] = useState(false)
  const thinkstoknow = [
    {
      title: "Single Family Property",
      points: [
        { text: "The entire property is designed for one household." },
        { text: "The owner has complete control over the property." },
        { text: "More privacy, space, and independence than multi-family homes." },
      ],
    },
    {
      title: "Multi Family Property",
      points: [
        { text: "Designed for multiple households." },
        { text: "May include duplexes, triplexes, or apartment buildings." },
        { text: "Owners may rent out units for income." },
      ],
    },
  ];

  const handleCancelAction = () => {
    // Close the popup after action
    setIsUnsavedChangesOpen(false)
  }

  const handleContinueWithoutSaving = () => {
    setIsUnsavedChangesOpen(false)
    navigate("/properties");
  }

  const onSubmit = async (data: UnitFormValues) => {
    try {
      setLoading(true);

      const propertyObject = {
        address: {
          streetAddress: data.address,
          city: data.city,
          state: data.state,
          zip: data.zip,
        },
        propertyTypeId: parseInt(data.propertyType),
      };

      const response = await apiHelper(
        PropertyService.postApiProperty,
        { showNotification: false },
        propertyObject
      );

      if (response?.data?.success) {
        if (data.unitImage) {
          const imgData = {
            EntityTypeId: DocumentEntityTypesEnum.Property,
            EntityId: Number(response.data.success.id),
            DocumentTypeId: DocumentTypesEnum.BeforeImage,
            IsMain: true,
            Document: data.unitImage,
            FileName: data.unitImage.name,
          };

          await apiHelper(DocumentService.postApiDocument, {
            successMessage: "Property Added Successfully",
            showNotification: true,
          }, imgData);
        }

        navigate(navigateto, { state: { id: response.data.success.id } });
        fetchData();
      } 
    } catch (error) {
      console.error("Submission failed", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPtyTypeId();
  }, []);

  return (
    <>
    <div className="d-flex flex-column gap-3 m-5">
      <Card>
        <div className="row">
          <div className="col-md-6">
            <FormProvider {...methods}>
              <form id="unitForm" onSubmit={handleSubmit(onSubmit)}>
                <div className="row">
                  <div className="col-md-12 mb-3 d-flex align-items-center gap-3">
                      <label className="fw-600 mb-0">
                      Property Type <span className="text-danger">*</span>
                    </label>
                  </div>
                  <div className="col-md-12 mb-2 mt-0 d-flex align-items-center gap-3">
                    <Controller
                      name="propertyType"
                      control={control}
                      rules={{ required: "Please select property type" }}
                      render={({ field }) => (
                        <>
                        
                          <Radio {...field} className="m-0" label="Single Family" value="1" />
                          <Radio {...field} className="m-0" label="Multi Family" value="2" />
                        </>
                      )}
                    />
                  </div>

                  <div className="col-md-12">
                    <Controller
                      name="unitImage"
                      control={control}
                      rules={{ required: "Unit image is required" }}
                      render={({ field: { onChange } }) => (
                        <Uploader
                          name="unitImage"
                          required
                          allowedFormats={[...JPEG_PNG_MIME_TYPES]}
                          allowedExtensions={[...JPEG_PNG_EXTENSIONS]}
                          maxSizeMB={2}
                        />
                      )}
                    />
                  </div>

                  

                  <div className="col-md-12">
                    <AddressAutocompleteRHF />
                  </div>
                  <div className="col-md-12">
                    <InputField
                      name="address"
                      label="Street Address"
                      required
                      placeholder="Enter Address"
                    />
                  </div>

                  <div className="col-md-4">
                    <InputField
                      name="city"
                      label="City"
                      required
                      placeholder="Enter City"
                      
                    />
                  </div>

                  <div className="col-md-4">
                    <InputField
                      name="state"
                      label="State"
                      required
                      placeholder="Enter State"
                    />
                  </div>

                  <div className="col-md-4">
                    <InputField
                      name="zip"
                      label="Zip Code"
                      required
                      placeholder="Enter Zip Code"
                      validation={{
                        pattern: {
                          value: /^\d{5}$/,
                          message: "Zip code must be 5 digits",
                        },
                      }}
                    />
                  </div>
                </div>

                
              </form>
            </FormProvider>

            
          </div>

          <div className="col-md-6">
            <div className="things-to-know-demo">
              <ThingsToKnow propertyTypes={thinkstoknow} />
            </div>
          </div>
        </div>
      </Card>

      
    </div>
    {/* Footer Buttons */}
            <FixedFooter
            onCancel={() => setIsUnsavedChangesOpen(true)}
            onSubmit={handleSubmit(onSubmit)}
            loading={loading}
            buttonLabel={"Save"}
            isSubmitDisabled={loading || !formState.isDirty || !formState.isValid}
            />


            {/* Unsaved Changes Popup */}
      <ConfirmationPopup
        isOpen={isUnsavedChangesOpen}
        title="Unsaved Changes"
        message="You have unsaved changes. If you go back or exit this step, your changes will be lost. Do you want to continue?"
        primaryButtonLabel="Cancel"
        secondaryButtonLabel="Continue Without Saving"
        onPrimaryAction={handleCancelAction}
        onSecondaryAction={handleContinueWithoutSaving}
        onClose={handleCancelAction}
        isPrimaryAction={false}
      />
    </>
  );
};

export default SelectProperty;
